import { useState, memo, useEffect } from 'react';

import { useMutation } from '@apollo/client';

import { useTheme } from 'styled-components';

import { WalletTypeEnum } from '@libs/backend-common';
import { useApiResponse } from '@libs/hooks';
import { CurrencyType } from '@libs/types';
import { Typography } from '@libs/components';

import { ActiveStarIcon, StarIcon } from 'src/assets';
import { useTranslation } from 'src/hooks';
import { addFavoriteAssetMutation, removeFavoriteAssetMutation } from 'src/gql';

import { StarIconWrapper } from './styles';

type FavoriteAssetIndicatorProps = {
  walletType?: WalletTypeEnum;
  isFavorite: boolean;
  assetCode: CurrencyType;
  shouldHaveLabel?: boolean;
  className?: string;
  refetch?: () => void;
};
const FavoriteAssetIndicator: React.FC<FavoriteAssetIndicatorProps> = ({
  isFavorite,
  assetCode,
  shouldHaveLabel,
  className,
  refetch,
}) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [isFavoriteAsset, setIsFavoriteAsset] = useState(isFavorite);

  useEffect(() => {
    setIsFavoriteAsset(isFavorite);
  }, [isFavorite]);

  const addFavoriteLabel = !isFavoriteAsset
    ? t('common_add_favorite')
    : t('common_remove_favorite');

  const additionAndDeletionArgs = {
    variables: {
      record: {
        assetCode,
      },
    },
  };

  const [handleRemoveFromFavorite, removeFromFavoriteRes] = useMutation(
    removeFavoriteAssetMutation
  );

  const [handleAddToFavorite, addToFavoritesRes] = useMutation(
    addFavoriteAssetMutation
  );
  const handleError = () => setIsFavoriteAsset((prev) => !prev);

  const { loading: addingResponseLoading } = useApiResponse(
    addToFavoritesRes,
    refetch,
    handleError
  );
  const { loading: removeResponseLoading } = useApiResponse(
    removeFromFavoriteRes,
    refetch,
    handleError
  );

  const isLoading = addingResponseLoading || removeResponseLoading;

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;
    setIsFavoriteAsset((prev) => !prev);
    if (isFavoriteAsset) {
      handleRemoveFromFavorite(additionAndDeletionArgs);
    } else {
      handleAddToFavorite(additionAndDeletionArgs);
    }
  };

  return (
    <StarIconWrapper
      shouldHaveLabel={shouldHaveLabel}
      isActive={isFavoriteAsset}
      onClick={handleStarClick}
      className={className}
    >
      {isFavoriteAsset ? <ActiveStarIcon /> : <StarIcon />}
      {shouldHaveLabel && (
        <Typography
          color={colors.text.secondary}
          type="bodyButton"
          fontFamily="primaryMedium"
        >
          {addFavoriteLabel}
        </Typography>
      )}
    </StarIconWrapper>
  );
};
export default memo(FavoriteAssetIndicator);
